/**
 * Stable grouping for the browser-side mirror of a server error.
 *
 * When an XHR fails, axios throws in whatever minified helper happens to be on
 * the stack — `Y8`, `G8`, `CT`, `settle`. Sentry groups on that frame, and the
 * name is regenerated by every build, so a single broken endpoint shows up as a
 * brand-new issue after each deploy while every one of those issues mixes
 * unrelated endpoints together. TOPBROKERCRM-214, -22P, -2KA and -2KB are all
 * the same non-signal: four buckets spanning `/marketplace`, `/leads/{id}`,
 * quotesheets, `/text-inbox` and a couple of 502 gateway blips.
 *
 * Grouping on the failed request instead gives one issue per broken endpoint,
 * stable across deploys. Ids are collapsed so a 500 on ten different leads is
 * one issue rather than ten.
 *
 * These events are deliberately NOT dropped: the server-side counterpart is not
 * always captured (the quotesheet 500 in TOPBROKERCRM-2KB has no PHP event at
 * all), and `docs/sentry-noise-filter.md` records "a real 500" as a must-survive
 * case.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Reduce a request URL to a route-shaped path: no origin, no query, no ids.
 *
 * @param {string|undefined|null} url
 * @returns {string}
 */
export function requestPath(url) {
    if (!url) {
        return 'unknown';
    }

    const path = String(url)
        .split('?')[0]
        .split('#')[0]
        .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');

    if (path === '') {
        return '/';
    }

    return path
        .split('/')
        .map((segment) => {
            if (/^\d+$/.test(segment)) {
                return ':id';
            }

            return UUID.test(segment) ? ':uuid' : segment;
        })
        .join('/');
}

/**
 * Grouping key + tags for an axios error that carries an HTTP response.
 * Returns null for anything else, so the caller leaves the event untouched.
 *
 * @param {any} error
 * @returns {{fingerprint: string[], tags: Record<string, string>}|null}
 */
export function axiosFingerprint(error) {
    const status = error?.response?.status;
    const config = error?.config;

    if (!status || !config) {
        return null;
    }

    const method = String(config.method || 'get').toUpperCase();
    const path = requestPath(config.url);

    return {
        fingerprint: ['axios', String(status), method, path],
        tags: { http_status: String(status), http_path: path },
    };
}
